// useUser.ts
import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';
import { User, Session } from '@supabase/supabase-js';
import { defaultConfig } from '../utils/config';
import { navigateToWebsite } from '../utils/urls';
import { useNavigate } from 'react-router-dom';

export interface UserData {
  id: string;
  email: string | undefined;
  name: string | undefined;
  session: Session | null;
  user_tag: string;
  profile_image_url: string;
  bio: string;
}

export const useUser = () => {
  const navigate = useNavigate();
  // Initialize userData to null. This prevents using stale localStorage data before the actual Supabase session is processed.
  const [userData, setUserData] = useState<UserData | null>(null);
  // Ensure loading is true initially until auth state is definitively known.
  const [loading, setLoading] = useState(true);

  const processUser = useCallback(async (user: User | null, session: Session | null, event: string) => {
    // console.log('USEUSER: processUser called. User param:', user ? 'present' : 'null', 'Session param:', session ? 'present' : 'null', 'Auth Event:', event);
    if (user) {
        setUserData({
          id: user.id,
          email: user.email || undefined,
          name: user.user_metadata?.full_name || user.user_metadata?.name || undefined,
          user_tag: user.user_metadata?.user_tag || '@',
          profile_image_url: user.user_metadata?.profile_image_url || '',
          bio: user.user_metadata?.bio || '',
          session: session,
        });
        localStorage.setItem('userEmail', user.email || '');
        localStorage.setItem('userId', user.id);
        // console.log('USEUSER: Setting final userData: with ID');
    } else {
        setUserData(null);
        // Clear user-specific local storage on logout or no user
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userId');
        localStorage.removeItem('subscription');
        // console.log('USEUSER: Setting final userData: null (no user)');
    }
    setLoading(false); // Always set loading to false after processing the user
    // console.log('USEUSER: processUser finished, setting loading to false.');
  }, []);

  const handleRedirectAfterLogout = useCallback((redirectPath?: string) => {
    // Clear user-specific local storage on log out
    localStorage.removeItem('redirectPath');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
    localStorage.removeItem('subscription');

    let finalRedirectPath = redirectPath;
    
    // Protected routes that would flash the login screen if reloaded:
    // - /game (always protected, even without ID)
    // - /game/:gameId
    // - /profile
    // - /art/:artId (but NOT /art alone)
    // - /3d/:artId
    // - /animate/:animationId (but NOT /animate alone)
    // - /test
    
    const isProtected = finalRedirectPath && (
      finalRedirectPath === '/profile' ||
      finalRedirectPath === '/test' ||
      finalRedirectPath === '/game' ||
      finalRedirectPath.startsWith('/game/') ||
      finalRedirectPath.startsWith('/art/') ||
      finalRedirectPath.startsWith('/3d/') ||
      finalRedirectPath.startsWith('/animate/')
    );
    
    // If redirect path is a protected route, go to home instead to avoid login flash
    if (isProtected) {
      finalRedirectPath = '/';
    }

    // console.log('app useUser: original redirect path:', finalRedirectPath);
    // console.log('app useUser: is protected:', isProtected);

    if (defaultConfig.REDIRECT_TO_WEBSITE) {
      // Add the logout parameter to the redirect URL
      finalRedirectPath = finalRedirectPath + '?logout=true';
      // console.log('app useUser: final redirect path:', finalRedirectPath);
      navigateToWebsite(finalRedirectPath);
    } else {
      // console.log('app useUser: final redirect path:', finalRedirectPath);
      navigate(finalRedirectPath);
    }
  }, [navigate]);

  const onLogOut = useCallback(async (redirectPath?: string) => {
    await supabase.auth.signOut();
    handleRedirectAfterLogout(redirectPath);
  }, [handleRedirectAfterLogout]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      // console.log('USEUSER: Initializing auth state via getSession.');
      setLoading(true); // Ensure loading is true while getting initial session
      const { data: { session: initialSession } } = await supabase.auth.getSession();
      if (mounted) {
        // console.log('USEUSER: Initial getSession result:', initialSession?.user ? 'user present' : 'no user');
        await processUser(initialSession?.user || null, initialSession || null, 'INITIAL_SESSION_LOAD');
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('USEUSER: onAuthStateChange event:', event, 'session user ID:', session?.user?.id);
      if (mounted) {
        if (event === 'SIGNED_OUT') {
          // Use functional update to check current state before redirecting
          // This prevents infinite loops when signOut() triggers this same event
          setUserData((current) => {
            if (current !== null) {
              // console.log('USEUSER: Sign out detected, triggering redirect.');
              handleRedirectAfterLogout();
            }
            return null;
          });
        } else {
          setLoading(true); // Ensure loading is true while processing any auth change
          processUser(session?.user || null, session || null, event);
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
        // console.log('USEUSER: Auth state change listener unsubscribed.');
      }
      mounted = false;
    };
  }, [processUser, handleRedirectAfterLogout]);

  // Add this log to see the user object returned by useUser
  useEffect(() => {
    // console.log('USEUSER: Current user state:', userData, 'Loading:', loading);
  }, [userData, loading]);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 means no rows found, which is fine for new users

      // Update userData with profile information if available
      setUserData(prev => {
        if (!prev) return null; // Should not happen if userId is present
        return {
          ...prev,
          name: profile?.name || undefined,
          user_tag: profile?.user_tag || '@',
          profile_image_url: profile?.profile_image_url || '',
          bio: profile?.bio || '',
        };
      });
      // console.log('USEUSER: Profile fetched and userData updated.');
      return profile;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  // Effect to fetch user profile when userData.id becomes available and stable
  useEffect(() => {
    if (userData?.id && !loading) { // Only fetch profile if user data is stable and not loading
      // console.log('USEUSER: Triggering fetchProfile for user ID:', userData.id);
      fetchProfile(userData.id);
    }
  }, [userData?.id, loading]); // Depend on userData.id and loading

  const updateUserMetadata = useCallback(async (metadata: { [key: string]: any }) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: metadata
      });

      if (error) throw error;

      if (data.user) {
        processUser(data.user, userData?.session ?? null, 'USER_METADATA_UPDATE'); // Passed event type
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating user metadata:', error);
      return { data: null, error };
    }
  }, [userData?.session, processUser]);

  // useEffect(() => {
  //   console.log('User data:', userData);
  // }, [userData]);

  return {
    user: userData,
    loading,
    updateUserMetadata,
    onLogOut
  };
};